import React, { createContext, memo, useContext, useEffect } from "react";
import { InertiaLink, usePage } from "@inertiajs/inertia-react";
import { ROLE_ID } from "@/Enums/roles";
import { TeamSidebarDropdown } from "@/Components/Team/TeamSidebarDropdown";
import classNames from "classnames";
import { Instagram, Linkedin, X, ArrowRight } from "react-feather";
import { ResultsDropdown } from "@/Components/Menu/ResultsDropdown";
import { AgencyClientsSidebarDropdown } from "@/Components/Team/AgencyClientsSidebarDropdown";

const NavigationContext = createContext();

const useNavigation = () => useContext(NavigationContext);

const NavigationProvider = ({ value, children }) => (
  <NavigationContext.Provider value={value}>
    {children}
  </NavigationContext.Provider>
);

const Navigation = memo(({ auth, isMobileMenuOpen, closeMobileMenu }) => {
  const { organization, agency } = usePage().props;
  const organizationData = organization?.data || null;
  const agencyData = agency?.data || null;

  useEffect(() => {
    const menuElement = document.getElementById("nav");
    if (isMobileMenuOpen) {
      const menuHeight = window.innerHeight;
      menuElement.style.maxHeight = `${menuHeight}px`;
    } else {
      menuElement.style.maxHeight = "none";
    }

    return () => {
      if (menuElement) menuElement.style.maxHeight = "none";
    };
  }, [isMobileMenuOpen]);

  return (
    <NavigationProvider value={{ isMobileMenuOpen, closeMobileMenu }}>
      <nav
        id="nav"
        className={classNames(
          "fixed bottom-0 left-0 top-0 z-[500] w-full flex-col gap-8 overflow-y-scroll bg-black p-4 lg:flex lg:w-[240px] 4xl:w-[300px]",
          { flex: isMobileMenuOpen, hidden: !isMobileMenuOpen },
        )}
      >
        <div className="flex justify-between">
          <InertiaLink className="leading-none" href={route("welcome")}>
            <img
              className="h-10 lg:max-h-[60px]"
              src={
                agencyData?.logo ||
                organizationData?.logo ||
                "/images/logo-large.png"
              }
              alt={agencyData?.name || "Gidås"}
            />
          </InertiaLink>
          <X
            color="white"
            size={40}
            className="cursor-pointer lg:hidden"
            onClick={closeMobileMenu}
          />
        </div>

        <RenderMenu auth={auth} />

        <MobileMenuFooter />

        <Footer />
      </nav>
    </NavigationProvider>
  );
});

const LinkWrapper = ({ text, href, activeRouteNames = [] }) => {
  const { isMobileMenuOpen, closeMobileMenu } = useNavigation();
  let { url } = usePage();

  const isActive =
    Array.isArray(activeRouteNames) && activeRouteNames.length > 0
      ? activeRouteNames.includes(route()?.current())
      : url.startsWith(href);

  return (
    <InertiaLink
      className={classNames(
        "rounded-sm px-4 py-3 text-[18px] font-bold leading-[130%]",
        {
          "bg-black text-white hover:bg-black-dark": !isActive,
          "bg-yellow text-black hover:bg-yellow": isActive,
        },
      )}
      href={href}
      onClick={() => isMobileMenuOpen && closeMobileMenu()}
    >
      {text}
    </InertiaLink>
  );
};

const RenderMenu = ({ auth }) => {
  const user = auth?.user?.data;

  const { closeMobileMenu } = useNavigation();

  if (user.memberships.length === 0) {
    return;
  }

  if (route()?.current()?.startsWith("admin.")) {
    return (
      <div className="flex flex-col gap-8 text-white">
        <MenuSection title="Overview">
          <LinkWrapper href={route("admin.agencies")} text="Global accounts" />
          <LinkWrapper href={route("admin.users")} text="Super users" />
        </MenuSection>
        <MenuSection title="Emma global">
          <LinkWrapper href={route("admin.factors")} text="Emission factors" />
          <LinkWrapper
            href={route("admin.question")}
            text="Global activities"
          />
        </MenuSection>
      </div>
    );
  }

  if (route()?.current()?.startsWith("agency.")) {
    return (
      <div className="flex flex-col gap-8 text-white">
        <MenuSection title="Overview">
          <LinkWrapper
            href={route("agency.home")}
            text="Home"
            activeRouteNames={["agency.home"]}
          />
          <AgencyClientsSidebarDropdown
            closeMobileNavigation={closeMobileMenu}
          />
        </MenuSection>
        <MenuSection title="Build">
          <LinkWrapper href={route("agency.factors")} text="Emission factors" />
          <LinkWrapper
            href={route("agency.question")}
            text="Global activities"
            activeRouteNames={["agency.question", "agency.question.edit"]}
          />
        </MenuSection>
      </div>
    );
  }

  if (user?.organization_role_id !== ROLE_ID.REPORTER) {
    return (
      <div className="flex flex-col gap-8 text-white">
        <MenuSection title="Overview">
          <LinkWrapper href="/welcome" text="Home" />
          <ResultsDropdown closeMobileNavigation={closeMobileMenu} />
        </MenuSection>
        <MenuSection title="Reporting">
          <TeamSidebarDropdown closeMobileNavigation={closeMobileMenu} />
          <LinkWrapper href="/library" text="Activity library" />
          <LinkWrapper href="/factors" text="Emission factors" />
          <LinkWrapper href="/categories" text="Categories" />
        </MenuSection>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 text-white">
      <MenuSection title="Overview">
        <LinkWrapper href="/welcome" text="Home" />
        <LinkWrapper href="/emission-accounting/dashboard" text="Results" />
        <LinkWrapper href="/emission-accounting/report" text="Report" />
      </MenuSection>
    </div>
  );
};

const MenuSection = ({ title, children }) => (
  <div className="flex flex-col">
    <span className="mb-2 text-sm font-bold uppercase text-[#c4c4c4]">
      {title}
    </span>
    {children}
  </div>
);

const MobileMenuFooter = () => {
  return (
    <div className="flex flex-col text-white lg:hidden">
      <span className="mb-2 text-sm font-bold uppercase text-[#c4c4c4]">
        Emma
      </span>
      <LinkWrapper href="/your-account" text="Your account" />
      <LinkWrapper href="/users" text="Manage users" />
      <LinkWrapper href="/company" text="Company settings" />
      <div className="mt-4 flex flex-col border-t border-white pt-4">
        <InertiaLink
          method="post"
          as="button"
          className="relative px-4 py-2 text-left text-[18px] font-bold leading-[130%] text-white hover:rounded-sm hover:bg-black"
          href="/logout"
        >
          Log out
          <ArrowRight className="absolute right-4 top-1/2 -translate-y-1/2" />
        </InertiaLink>
      </div>
    </div>
  );
};

const Footer = () => (
  <div className="mt-auto hidden flex-col text-white lg:flex">
    <InertiaLink className="text-4xl font-bold" href="/welcome">
      <img
        className="h-10 w-10"
        src="/images/logo-large.png"
        alt="Gidås logo"
      />
    </InertiaLink>
    <p className="mb-4 mt-2 font-overpass text-sm">
      En del av Gidås hållbarhetsbyrå AB och Dille Bolagen &ndash; Östersund
    </p>
    <a
      href="mailto:hej@gidas.se"
      target="_blank"
      className="mb-2 text-sm hover:underline"
    >
      hej@gidas.se
    </a>
    <InertiaLink
      className="mb-2 text-sm hover:underline"
      href="/terms-of-service"
    >
      Terms and conditions
    </InertiaLink>
    <ul className="mt-4 flex gap-4">
      <li>
        <a href="https://www.linkedin.com/company/gidasagency/" target="_blank">
          <Linkedin size={24} className="mb-1 inline-block" />
        </a>
      </li>
      <li>
        <a
          href="https://www.instagram.com/gidashallbarhetsbyra/"
          target="_blank"
        >
          <Instagram size={24} className="mb-1 inline-block" />
        </a>
      </li>
    </ul>
  </div>
);

export default Navigation;
